import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Clock } from "lucide-react";
import { BLOG, IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, blogSchema, blogPostSchema, buildBlogPostSEO, breadcrumbSchema } from "../data/seo";

export const BlogPage = () => (
  <div data-testid="blog-page">
    <Seo
      title={SEO.journal.title}
      description={SEO.journal.description}
      schemas={[
        blogSchema(),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Journal", path: "/journal" }]),
      ]}
    />
    <section className="relative h-[30vh] min-h-[340px] overflow-hidden">
      <img src={IMAGES.clinicInterior3} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/65" />
      <div className="relative h-full container-wide flex flex-col justify-end pb-16 text-white">
        <div className="label-eyebrow text-gold mb-6">The Smile Journal</div>
        <h1 className="heading-display text-5xl md:text-7xl text-white max-w-3xl">
          Notes from the <span className="italic text-gold-light">studio.</span>
        </h1>
      </div>
    </section>

    <section className="py-24 md:py-32 container-x">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {BLOG.map((p, i) => (
          <Link key={p.slug} to={`/journal/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group reveal" style={{ transitionDelay: `${i * 80}ms` }}>
            <div className="aspect-[4/3] overflow-hidden mb-6">
              <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
            </div>
            <div className="flex items-center gap-4 text-[10px] tracking-[0.3em] uppercase text-ink/50">
              <span className="text-gold">{p.category}</span>
              <span>{p.date}</span>
              <span className="flex items-center gap-1"><Clock size={12} strokeWidth={1.2} /> {p.readTime}</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl font-light mt-4 leading-tight group-hover:text-gold transition-colors">{p.title}</h3>
            <p className="text-ink/60 mt-3 leading-relaxed">{p.excerpt}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b border-ink pb-1 group-hover:text-gold group-hover:border-gold transition-colors">
              Read <ArrowUpRight size={14} strokeWidth={1.4} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  </div>
);

export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/journal" replace />;

  const seoData = buildBlogPostSEO(post);

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={seoData.title}
        description={seoData.description}
        image={post.image}
        type="article"
        schemas={[
          blogPostSchema(post),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Journal", path: "/journal" },
            { name: post.title, path: `/journal/${post.slug}` },
          ]),
        ]}
      />
      <article className="pt-32 md:pt-40 pb-24">
        <div className="container-x max-w-3xl mx-auto">
          <nav className="text-[11px] tracking-[0.3em] uppercase text-ink/50 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={12} />
            <Link to="/journal" className="hover:text-gold">Journal</Link>
          </nav>
          <div className="label-eyebrow mb-6 text-gold">{post.category}</div>
          <h1 className="heading-display text-4xl md:text-6xl">{post.title}</h1>
          <div className="flex items-center gap-4 mt-8 text-[11px] tracking-[0.3em] uppercase text-ink/50">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime} read</span>
          </div>
        </div>
        <div className="container-x max-w-5xl mx-auto mt-16">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="container-x max-w-2xl mx-auto mt-16 text-lg leading-[1.9] text-ink/80 space-y-6">
          <p className="text-2xl font-serif italic text-ink leading-snug">{post.excerpt}</p>
          <p>The first thing I tell every patient who comes to me asking about veneers: the best ones look like teeth. Not like Hollywood. Not like a row of perfect Chiclets. Like your teeth — only better.</p>
          <p>That difference comes from a hundred small decisions that get made before we ever touch enamel. The shape of each tooth relative to the curve of your lip. The way light should pass through the incisal edge. The shade gradient from gum to tip. The microscopic surface texture that catches light the way natural enamel does.</p>
          <p>These are the details that no one notices when they're done well — and everyone notices when they're not. The "veneer look," as patients call it, is what happens when those details are skipped. When everything is the same flat shade. When the shapes are templated. When the porcelain is too opaque and the result feels like a costume.</p>
          <p>At Smiles by Dr. K, we work with master ceramists who hand-layer each veneer. We design every case digitally before we begin. And we always — always — let you preview your future smile before anything is permanent.</p>
          <p>Because the best veneers shouldn't announce themselves. They should just feel like home.</p>
        </div>
        <div className="container-x max-w-2xl mx-auto mt-16 pt-12 border-t border-ink/10">
          <Link to="/journal" className="text-[11px] tracking-[0.3em] uppercase link-underline">← Back to Journal</Link>
        </div>
      </article>
    </div>
  );
};
